import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { PageSectionCard } from "../../components/layout";
import { workspaceSupportService, type SupportEntitlement } from "../../services/customerPlanService";
import { REACT_APP_SUPPORT_URL } from "../../config/runtimeConfig";

const date = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : "—");

/** Support bought for this workspace: what is left, until when, and the reply windows. */
export default function WorkspaceSupportCard() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SupportEntitlement[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    workspaceSupportService
      .list()
      .then((data) => !cancelled && setRows(Array.isArray(data) ? data : []))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNavigateToSupport = () => {
    window.open(REACT_APP_SUPPORT_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <PageSectionCard title={t("workspaceSupport.title")} description={t("workspaceSupport.description")}>
      <Stack spacing={2}>
        {error ? (
          <Alert severity="error">{t("workspaceSupport.loadError")}</Alert>
        ) : rows == null ? (
          <CircularProgress size={24} />
        ) : rows.length === 0 ? (
          <Alert severity="info">{t("workspaceSupport.none")}</Alert>
        ) : (
          <Stack spacing={1.5}>
            {rows.map((e) => (
              <Box key={e.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={650}>
                    {e.packageName}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={e.usable ? "success" : "default"}
                    label={t(`workspaceSupport.status.${e.usable ? "ACTIVE" : e.status}`)}
                  />
                </Stack>
                <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "1fr auto", columnGap: 2, rowGap: 0.25 }}>
                  {e.tickets != null && (
                    <>
                      <Typography component="dt" variant="body2" color="text.secondary">
                        {t("workspaceSupport.ticketsLeft")}
                      </Typography>
                      <Typography component="dd" variant="body2" fontWeight={600} sx={{ m: 0, textAlign: "right" }}>
                        {e.ticketsRemaining} / {e.tickets}
                      </Typography>
                    </>
                  )}
                  {e.meetingHours != null && (
                    <>
                      <Typography component="dt" variant="body2" color="text.secondary">
                        {t("workspaceSupport.meetingLeft")}
                      </Typography>
                      <Typography component="dd" variant="body2" fontWeight={600} sx={{ m: 0, textAlign: "right" }}>
                        {t("workspaceSupport.minutes", { left: e.meetingMinutesRemaining ?? 0, total: e.meetingHours * 60 })}
                      </Typography>
                    </>
                  )}
                  {e.ticketResponse && (
                    <>
                      <Typography component="dt" variant="body2" color="text.secondary">
                        {t("superAdmin.support.ticketResponse")}
                      </Typography>
                      <Typography component="dd" variant="body2" sx={{ m: 0, textAlign: "right" }}>
                        {e.ticketResponse}
                      </Typography>
                    </>
                  )}
                  {e.meetingResponse && (
                    <>
                      <Typography component="dt" variant="body2" color="text.secondary">
                        {t("superAdmin.support.meetingResponse")}
                      </Typography>
                      <Typography component="dd" variant="body2" sx={{ m: 0, textAlign: "right" }}>
                        {e.meetingResponse}
                      </Typography>
                    </>
                  )}
                  <Typography component="dt" variant="body2" color="text.secondary">
                    {t("workspaceSupport.validUntil")}
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0, textAlign: "right" }}>
                    {date(e.expiresAt)}
                  </Typography>
                </Box>
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary">
              {t("workspaceSupport.footnote")}
            </Typography>
          </Stack>
        )}
        <Box>
          <Button
            variant="contained"
            color="primary"
            endIcon={<OpenInNewIcon />}
            onClick={handleNavigateToSupport}
          >
            {t("workspaceSupport.goToSupportPortal")}
          </Button>
        </Box>
      </Stack>
    </PageSectionCard>
  );
}
